/**
 * tm/components/TemplatePicker.tsx — 업무 템플릿 찾아 고르기 (2026-10-29 지도사 「템플릿 검색도 되고 · 편집도 되고 · 추가도 되게 여기서」)
 *  - 고른 템플릿이 보이는 칸을 누르면 찾기 칸 + 목록(아래로 펼침 · 창 안에서 잘리지 않게 흐름 안에 둠)
 *  - 찾는 말은 이름 · 업무 유형(cat_path) 모두에서 · 띄어쓰기 · 대소문자 무시 · 앞이 맞는 것 먼저
 *  - 맨 아래 「+ 새 템플릿 만들기」 — 찾던 말을 이름으로 넘김(onCreate)
 *  - ↑ ↓ 로 옮기고 Enter 로 고름 · Esc 로 닫음
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { TaskTemplate } from '../types';

const K = { accent: '#2563EB', accentLt: '#EFF6FF', body: '#374151', sub: '#6B7280', muted: '#9CA3AF', border: '#E5E7EB', bg: '#F8FAFC', white: '#FFFFFF' };
const norm = (s: string) => s.replace(/\s+/g, '').toLowerCase();
const groupOf = (t: TaskTemplate) => (Array.isArray(t.cat_path) && t.cat_path[0]) || '기타';

interface Props {
  templates: TaskTemplate[];
  value: string;
  onPick: (id: string) => void;
  onCreate: (name: string) => void;
  /** 이 창에서 고친 이름 등 — 없으면 t.name */
  nameOf?: (t: TaskTemplate) => string;
}

type Row = { kind: 'tpl'; tpl: TaskTemplate; head?: string } | { kind: 'new' };

export default function TemplatePicker({ templates, value, onPick, onCreate, nameOf }: Props) {
  const name = useCallback((t: TaskTemplate) => (nameOf ? nameOf(t) : t.name), [nameOf]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [hi, setHi] = useState(0);
  const listRef = useRef<HTMLDivElement | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const cur = templates.find(t => t.id === value);
  const active = useMemo(() => templates.filter(t => t.is_active), [templates]);

  const rows: Row[] = useMemo(() => {
    const k = norm(q);
    let list: Row[];
    if (!k) {
      // 찾는 말이 없으면 업무 유형별로 묶어서(유형 이름 가나다 · 기타는 맨 뒤)
      const groups = new Map<string, TaskTemplate[]>();
      active.forEach(t => { const g = groupOf(t); groups.set(g, [...(groups.get(g) || []), t]); });
      const keys = Array.from(groups.keys()).sort((a, b) => (a === '기타' ? 1 : b === '기타' ? -1 : a.localeCompare(b, 'ko')));
      list = keys.flatMap(g => (groups.get(g) || []).map((tpl, i) => ({ kind: 'tpl' as const, tpl, head: i === 0 ? g : undefined })));
    } else {
      const scored = active
        .map(tpl => { const n = norm(name(tpl)); const c = norm((tpl.cat_path || []).join(' ')); return { tpl, s: n.startsWith(k) ? 0 : n.includes(k) ? 1 : c.includes(k) ? 2 : -1 }; })
        .filter(x => x.s >= 0)
        .sort((a, b) => a.s - b.s || name(a.tpl).localeCompare(name(b.tpl), 'ko'));
      list = scored.map(x => ({ kind: 'tpl' as const, tpl: x.tpl }));
    }
    return [...list, { kind: 'new' }];
  }, [q, active, name]);

  useEffect(() => { setHi(0); }, [q]);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => { if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);
  useEffect(() => {   // 고른 줄이 보이게
    const el = listRef.current && listRef.current.querySelector<HTMLElement>(`[data-i="${hi}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [hi]);

  const choose = (r: Row) => {
    setOpen(false); setQ('');
    if (r.kind === 'new') onCreate(q.trim()); else onPick(r.tpl.id);
  };
  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi(h => Math.min(rows.length - 1, h + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi(h => Math.max(0, h - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); const r = rows[hi]; if (r) choose(r); }
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setOpen(false); setQ(''); }
  };

  const field: React.CSSProperties = { width: '100%', boxSizing: 'border-box', height: 26, padding: '0 8px', borderRadius: 5, fontSize: 12, outline: 'none', fontFamily: 'inherit' };
  const nTpl = rows.length - 1;

  return (
    <div ref={boxRef} style={{ flex: 1, minWidth: 0 }}>
      {open ? (
        <input autoFocus value={q} onChange={e => setQ(e.target.value)} onKeyDown={onKey} aria-label="템플릿 찾기"
          placeholder={`템플릿 찾기 — 이름 · 업무 유형 (${active.length}개)`}
          style={{ ...field, border: `1px solid ${K.accent}`, background: K.white, color: K.body }} />
      ) : (
        <button type="button" onClick={() => setOpen(true)} aria-haspopup="listbox"
          style={{ ...field, display: 'flex', alignItems: 'center', gap: 6, textAlign: 'left', cursor: 'pointer', border: `0.5px solid ${cur ? '#85B7EB' : K.border}`, background: cur ? K.accentLt : K.white, color: cur ? K.accent : K.sub, fontWeight: cur ? 600 : 400 }}>
          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cur ? `${cur.icon || '📋'} ${name(cur)}` : '템플릿 찾기 · 고르기'}</span>
          <span aria-hidden style={{ color: K.muted, fontSize: 10 }}>▼</span>
        </button>
      )}
      {open && (
        <div ref={listRef} role="listbox" style={{ marginTop: 4, maxHeight: 260, overflowY: 'auto', border: `1px solid ${K.border}`, borderRadius: 6, background: K.white }}>
          {nTpl === 0 && <div style={{ padding: '8px 10px', fontSize: 12, color: K.muted }}>「{q.trim()}」 맞는 템플릿이 없어요</div>}
          {rows.map((r, i) => (
            <React.Fragment key={r.kind === 'tpl' ? r.tpl.id : 'new'}>
              {r.kind === 'tpl' && r.head && <div style={{ padding: '6px 10px 2px', fontSize: 10, fontWeight: 700, color: K.muted, background: K.bg }}>{r.head}</div>}
              <div data-i={i} role="option" aria-selected={i === hi}
                onMouseEnter={() => setHi(i)} onMouseDown={e => { e.preventDefault(); choose(r); }}
                style={{ padding: '6px 10px', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, background: i === hi ? K.accentLt : K.white,
                  color: r.kind === 'new' ? K.accent : K.body, fontWeight: r.kind === 'new' || (r.kind === 'tpl' && r.tpl.id === value) ? 600 : 400,
                  borderTop: r.kind === 'new' ? `0.5px solid ${K.border}` : 'none' }}>
                {r.kind === 'tpl' ? (
                  <>
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.tpl.icon || '📋'} {name(r.tpl)}</span>
                    <span style={{ fontSize: 10, color: K.muted, flexShrink: 0 }}>{q ? `${groupOf(r.tpl)} · ` : ''}{(r.tpl.steps || []).length}단계</span>
                  </>
                ) : <span>+ {q.trim() ? `「${q.trim()}」 새 템플릿 만들기` : '새 템플릿 만들기'}</span>}
              </div>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}
